<script setup lang="ts">
import PageHeader from '@/components/Globals/PageHeader.vue'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import ListingFilters from '@/components/Globals/ListingFilters.vue'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import CompanyUserModal from './modals/CompanyUserModal.vue'
import ConfirmationModal from '@/components/Globals/ConfirmationModal.vue'
import IcDeleteClose from '@/components/Icons/IcDeleteClose.vue'
import { useUserStore } from '@/store/user'
import { useSuccessStore } from '@/store/success'
import UserResource from '@/api/resources/user'
import { computed, onMounted, ref } from 'vue'
import { matchesSearchAccentInsensitive } from '@/utils/string/search-normalize'
import { buildCompanyUserUpdatePayload } from '@/utils/user/company-user-update'
import type { IUserLight } from '@/interfaces/entities/user'
import { translateUserRole } from '@/interfaces/enums'
import { formatOutsideRegularUtcTimeToLocalHHmm } from '@/utils/date/date-time'

const isLoading = ref(true)
const store = useUserStore()
const users = computed(() => store.users)
const searchQuery = ref('')

const selectedRole = ref<string>('')
const selectedReceiveAlerts = ref<string>('')
const selectedReceivePush = ref<string>('')
const selectedReceiveWhatsapp = ref<string>('')
const selectedReceiveEmail = ref<string>('')
const selectedHasShift = ref<string>('')

const isModalOpen = ref(false)
const selectedUserId = ref<number | null>(null)

const isRemoveModalOpen = ref(false)
const selectedUserForDelete = ref<{ id: number; name: string } | null>(null)
const isDeleting = ref(false)
const isTogglingAlerts = ref<Record<number, boolean>>({})

type NotificationToggleField = 'receive_alerts' | 'receive_push' | 'receive_email' | 'receive_whatsapp'

async function toggleNotification(user: IUserLight, field: NotificationToggleField) {
  if (isTogglingAlerts.value[user.id]) return

  if (field !== 'receive_alerts' && !user.receive_alerts) return

  isTogglingAlerts.value[user.id] = true
  const previousValue = user[field]
  const newValue = !previousValue

  user[field] = newValue

  try {
    await store.updateCompanyUser(buildCompanyUserUpdatePayload(user))
    useSuccessStore().setSuccess(['Preferência de notificação atualizada.'])
  } catch (error) {
    user[field] = previousValue
    console.error('Falha ao atualizar status de alertas:', error)
  } finally {
    isTogglingAlerts.value[user.id] = false
  }
}

function openCreateModal() {
  selectedUserId.value = null
  isModalOpen.value = true
}

function openEditModal(id: number) {
  selectedUserId.value = id
  isModalOpen.value = true
}

function closeModal() {
  isModalOpen.value = false
  selectedUserId.value = null
}

function openRemoveModal(user: { id: number; name: string }) {
  selectedUserForDelete.value = user
  isRemoveModalOpen.value = true
}

function closeRemoveModal() {
  isRemoveModalOpen.value = false
  selectedUserForDelete.value = null
  isDeleting.value = false
}

async function deleteUser() {
  if (!selectedUserForDelete.value) return
  
  isDeleting.value = true
  try {
    await UserResource.removeUser(selectedUserForDelete.value.id)
    useSuccessStore().setSuccess(['Usuário removido com sucesso.'])
    handleRefresh()
  } catch (error) {
    console.error('Failed to delete user:', error)
  } finally {
    closeRemoveModal()
  }
}

const filteredUsers = computed(() => {
  let result = users.value

  if (selectedRole.value) {
    result = result.filter((u) => u.role === selectedRole.value)
  }

  if (selectedReceiveAlerts.value) {
    const wantsAlerts = selectedReceiveAlerts.value === 'yes'
    result = result.filter((u) => !!u.receive_alerts === wantsAlerts)
  }

  if (selectedReceivePush.value) {
    const wantsPush = selectedReceivePush.value === 'yes'
    result = result.filter((u) => !!u.receive_push === wantsPush)
  }

  if (selectedReceiveWhatsapp.value) {
    const wantsWhatsapp = selectedReceiveWhatsapp.value === 'yes'
    result = result.filter((u) => !!u.receive_whatsapp === wantsWhatsapp)
  }

  if (selectedReceiveEmail.value) {
    const wantsEmail = selectedReceiveEmail.value === 'yes'
    result = result.filter((u) => !!u.receive_email === wantsEmail)
  }

  if (selectedHasShift.value) {
    const wantsShift = selectedHasShift.value === 'yes'
    result = result.filter((u) => wantsShift ? !!(u.shift_id || u.shift) : !(u.shift_id || u.shift))
  }

  const q = searchQuery.value.trim()
  if (q) {
    result = result.filter((u) => matchesSearchAccentInsensitive(u.name, q) || matchesSearchAccentInsensitive(u.email, q))
  }

  return result
})

onMounted(() => {
  store.fetchUsers().then(() => {
    isLoading.value = false
  })
})

function handleRefresh() {
  isLoading.value = true
  store.fetchUsers().then(() => {
    isLoading.value = false
  })
}

function formatTime(timeStr?: string) {
  if (!timeStr) return ''
  return formatOutsideRegularUtcTimeToLocalHHmm(timeStr) || timeStr
}

function formatPhoneNumber(phone?: string) {
  if (!phone) return 'Sem telefone'
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 13 && cleaned.startsWith('55')) {
    return `+55 (${cleaned.substring(2, 4)}) ${cleaned.substring(4, 9)}-${cleaned.substring(9)}`
  }
  if (cleaned.length === 11) {
    return `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 7)}-${cleaned.substring(7)}`
  }
  if (cleaned.length === 10) {
    return `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 6)}-${cleaned.substring(6)}`
  }
  return phone
}
</script>

<template>
  <DefaultLayout>
    <Loader v-if="isLoading" />

    <div v-else class="mx-auto w-full max-w-[1400px] px-4 py-6">
      <PageHeader
        :to="{ name: 'dashboard' }"
        title="Usuários da Empresa"
        subtitle="Lista de todos os usuários vinculados à sua empresa.">
        <template #actions>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="flex items-center justify-center border border-stroke p-2 text-slate-700 transition hover:bg-slate-50"
              title="Atualizar"
              @click="handleRefresh">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <ListingFilters>
              <template #default="{ close }">
                <input
                  v-model="searchQuery"
                  type="text"
                  class="mb-3 w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none"
                  placeholder="Buscar por nome ou email"
                  @keydown.enter="close" />
                  
                <select
                  v-model="selectedRole"
                  class="mb-3 w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none">
                  <option value="">Todos os níveis de acesso</option>
                  <option value="admin">Administrador</option>
                  <option value="manager">Gestor</option>
                  <option value="technical">Técnico</option>
                </select>

                <div class="mb-3 border-t border-stroke pt-3">
                  <span class="mb-2 block text-xs font-semibold text-slate-500 uppercase">Status de Notificações</span>
                  <select
                    v-model="selectedReceiveAlerts"
                    class="mb-2 w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none">
                    <option value="">Geral (Todos)</option>
                    <option value="yes">Geral: Ativo</option>
                    <option value="no">Geral: Inativo</option>
                  </select>
                  <select
                    v-model="selectedReceivePush"
                    class="mb-2 w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none">
                    <option value="">Push (Todos)</option>
                    <option value="yes">Push: Ativo</option>
                    <option value="no">Push: Inativo</option>
                  </select>
                  <select
                    v-model="selectedReceiveWhatsapp"
                    class="mb-2 w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none">
                    <option value="">WhatsApp (Todos)</option>
                    <option value="yes">WhatsApp: Ativo</option>
                    <option value="no">WhatsApp: Inativo</option>
                  </select>
                  <select
                    v-model="selectedReceiveEmail"
                    class="mb-3 w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none">
                    <option value="">Email (Todos)</option>
                    <option value="yes">Email: Ativo</option>
                    <option value="no">Email: Inativo</option>
                  </select>
                </div>

                <select
                  v-model="selectedHasShift"
                  class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none">
                  <option value="">Todos (Status de turno)</option>
                  <option value="yes">Com turno definido</option>
                  <option value="no">Sem turno definido</option>
                </select>
              </template>
            </ListingFilters>
            <button
              type="button"
              class="border border-primary bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary/90"
              @click="openCreateModal">
              Novo Usuário
            </button>
          </div>
        </template>
      </PageHeader>
      
      <div class="mt-6 flex flex-col gap-4">
        <div v-for="user in filteredUsers" :key="user.id" class="border border-stroke bg-white p-4 shadow-sm transition hover:shadow-md">
          <div class="flex flex-col sm:flex-row sm:items-stretch sm:justify-between">
            <div class="flex-1">
              <h3 class="text-lg font-bold text-black-2">{{ user.name }}</h3>
              <div class="mt-3 text-sm text-slate-600 space-y-1">
                <p>
                  Email:
                  <span class="font-bold text-black-2">
                    {{ user.email }}
                  </span>
                </p>
                <p>
                  Telefone: 
                  <span class="font-bold text-black-2">
                    {{ formatPhoneNumber(user.phone_number) }}
                  </span>
                </p>
                <p>
                  Último acesso: 
                  <span class="font-bold text-black-2">
                    {{ user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString('pt-BR') : 'Nunca acessou' }}
                  </span>
                </p>
                <div class="mt-2 flex flex-col gap-2">
                  <span class="text-xs font-semibold text-slate-500 uppercase">Notificações:</span>
                  <div class="flex flex-wrap items-center gap-4">
                    <!-- Alertas Geral -->
                    <div class="flex items-center gap-2">
                      <span class="text-xs">Geral:</span>
                      <button 
                        type="button" 
                        :class="user.receive_alerts ? 'bg-primary' : 'bg-slate-300'" 
                        class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer border border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50" 
                        @click="toggleNotification(user, 'receive_alerts')"
                        :disabled="isTogglingAlerts[user.id]"
                        :title="user.receive_alerts ? 'Desativar alertas' : 'Ativar alertas'"
                      >
                        <span 
                          aria-hidden="true" 
                          :class="user.receive_alerts ? 'translate-x-4' : 'translate-x-0'" 
                          class="pointer-events-none inline-block h-4 w-4 transform bg-white shadow ring-0 transition duration-200 ease-in-out"
                        ></span>
                      </button>
                    </div>
                    <!-- Push -->
                    <div class="flex items-center gap-2" :class="{ 'opacity-50': !user.receive_alerts }">
                      <span class="text-xs">Push:</span>
                      <button 
                        type="button" 
                        :class="user.receive_push ? 'bg-blue-600' : 'bg-slate-300'" 
                        class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer border border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50" 
                        @click="toggleNotification(user, 'receive_push')"
                        :disabled="!user.receive_alerts || isTogglingAlerts[user.id]"
                        :title="user.receive_push ? 'Desativar Push' : 'Ativar Push'"
                      >
                        <span 
                          aria-hidden="true" 
                          :class="user.receive_push ? 'translate-x-4' : 'translate-x-0'" 
                          class="pointer-events-none inline-block h-4 w-4 transform bg-white shadow ring-0 transition duration-200 ease-in-out"
                        ></span>
                      </button>
                    </div>
                    <!-- WhatsApp -->
                    <div class="flex items-center gap-2" :class="{ 'opacity-50': !user.receive_alerts }">
                      <span class="text-xs">WhatsApp:</span>
                      <button 
                        type="button" 
                        :class="user.receive_whatsapp ? 'bg-green-500' : 'bg-slate-300'" 
                        class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer border border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50" 
                        @click="toggleNotification(user, 'receive_whatsapp')"
                        :disabled="!user.receive_alerts || isTogglingAlerts[user.id]"
                        :title="user.receive_whatsapp ? 'Desativar WhatsApp' : 'Ativar WhatsApp'"
                      >
                        <span 
                          aria-hidden="true" 
                          :class="user.receive_whatsapp ? 'translate-x-4' : 'translate-x-0'" 
                          class="pointer-events-none inline-block h-4 w-4 transform bg-white shadow ring-0 transition duration-200 ease-in-out"
                        ></span>
                      </button>
                    </div>
                    <!-- Email -->
                    <div class="flex items-center gap-2" :class="{ 'opacity-50': !user.receive_alerts }">
                      <span class="text-xs">Email:</span>
                      <button 
                        type="button" 
                        :class="user.receive_email ? 'bg-orange-500' : 'bg-slate-300'" 
                        class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer border border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50" 
                        @click="toggleNotification(user, 'receive_email')"
                        :disabled="!user.receive_alerts || isTogglingAlerts[user.id]"
                        :title="user.receive_email ? 'Desativar Email' : 'Ativar Email'"
                      >
                        <span 
                          aria-hidden="true" 
                          :class="user.receive_email ? 'translate-x-4' : 'translate-x-0'" 
                          class="pointer-events-none inline-block h-4 w-4 transform bg-white shadow ring-0 transition duration-200 ease-in-out"
                        ></span>
                      </button>
                    </div>
                  </div>
                </div>
                <p>
                  Turno: 
                  <span class="font-bold text-black-2">
                    <template v-if="user.shift && user.shift.name">
                      {{ user.shift.name }} ({{ formatTime(user.shift.start_time) }} - {{ formatTime(user.shift.end_time) }})
                    </template>
                    <template v-else>
                      Não tem turno
                    </template>
                  </span>
                </p>
              </div>
            </div>
            <div class="mt-4 flex flex-col items-start sm:mt-0 sm:items-end sm:justify-between">
              <div class="flex items-center gap-2">
                <button type="button" @click="openEditModal(user.id)" class="text-primary hover:text-primary/80" title="Editar Usuário">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                </button>
                <button type="button" @click="openRemoveModal(user)" class="text-red hover:text-red/80" title="Deletar Usuário">
                  <IcDeleteClose width="20" height="20" />
                </button>
              </div>
              <span v-if="user.role" class="mt-auto inline-block bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                {{ translateUserRole(user.role) }}
              </span>
            </div>
          </div>
        </div>
        
        <div v-if="filteredUsers.length === 0" class="py-8 text-center text-slate-500 border border-stroke bg-white shadow-sm">
          Nenhum usuário encontrado.
        </div>
      </div>
    </div>
    
    <CompanyUserModal
      :isOpen="isModalOpen"
      :userId="selectedUserId"
      @close="closeModal"
      @saved="handleRefresh"
    />
    
    <ConfirmationModal
      :isOpen="isRemoveModalOpen"
      cancelText="Cancelar"
      confirmationText="Deletar"
      @cancel="closeRemoveModal"
      @add="deleteUser"
    >
      <template #content>
        <div class="text-center">
          <h3 class="text-lg font-medium text-black-2">Confirmar Exclusão</h3>
          <p class="mt-2 text-sm text-slate-500">
            Tem certeza que deseja deletar o usuário <span class="font-bold">{{ selectedUserForDelete?.name }}</span>?
            <br />
            Esta ação não poderá ser desfeita.
          </p>
        </div>
      </template>
    </ConfirmationModal>
  </DefaultLayout>
</template>
